import type { ZudokuConfig } from "zudoku";
import BabelShark, {
	BabelSharkListItem,
	BabelSharkParagraph,
} from "./src/components/BabelShark";
import AudienceBadge from "./src/components/AudienceBadge";
import Mermaid from "./src/components/Mermaid";
import { withAudienceBadges } from "./src/openapi/audienceBadges";
import publicOpenApi from "./apis/public-openapi.json";
import "./styles.css";

// zudoku's Vite plugin only inlines literal `process.env.NAME` dot-access for
// the client bundle — a dynamic `process.env[name]` lookup is never replaced
// and evaluates against an empty object in the browser. Each variable must be
// read as its own static expression.
const requireEnvironmentVariable = (name: string, value: string | undefined): string => {
	const trimmed = value?.trim();
	if(!trimmed) {
		throw new Error(`${name} is required to protect /dev-docs`);
	}
	return trimmed;
};

const environmentUrlOrigin = (
	name: string,
	value: string | undefined,
	fallback: string,
): string => {
	const candidate = value?.trim() || fallback;

	try {
		const url = new URL(candidate);
		if(
			!["http:", "https:"].includes(url.protocol) ||
      url.username ||
      url.password ||
      url.pathname !== "/" ||
      url.search ||
      url.hash
		) {
			throw new Error();
		}
		return url.origin;
	} catch{
		throw new Error(`${name} must be an HTTP(S) origin, for example https://docs.example.com`);
	}
};

const zitadelIssuer = requireEnvironmentVariable(
	"ZUDOKU_PUBLIC_ZITADEL_ISSUER",
	process.env.ZUDOKU_PUBLIC_ZITADEL_ISSUER,
);
const zitadelClientId = requireEnvironmentVariable(
	"ZUDOKU_PUBLIC_ZITADEL_CLIENT_ID",
	process.env.ZUDOKU_PUBLIC_ZITADEL_CLIENT_ID,
);
const siteOrigin = environmentUrlOrigin(
	"ZUDOKU_PUBLIC_SITE_ORIGIN",
	process.env.ZUDOKU_PUBLIC_SITE_ORIGIN,
	"https://help.onwardinc.net",
);

const darkTheme = {
	background: "#0f0f0f",
	foreground: "#fafafa",
	card: "#191919",
	cardForeground: "#fafafa",
	popover: "#191919",
	popoverForeground: "#fafafa",
	primary: "#ffb872",
	primaryForeground: "#3b3028",
	secondary: "#262626",
	secondaryForeground: "#fafafa",
	muted: "#262626",
	mutedForeground: "#999999",
	accent: "#3b82f6",
	accentForeground: "#ffffff",
	destructive: "#ef4444",
	destructiveForeground: "#ffffff",
	border: "#333333",
	input: "#2e2e2e",
	ring: "#ffb872",
	radius: "0.5rem",
};

const config: ZudokuConfig = {
	slots: {
		"head-navigation-end": BabelShark,
	},
	site: {
		title: "Onward Documentation",
		logo: {
			src: { light: "/logo-light.svg", dark: "/logo-dark.svg" },
			alt: "Onward",
			width: "150px",
		},
		showPoweredBy: false,
	},
	metadata: {
		title: "Onward Documentation",
		description: "Integration guides and public API reference for Onward Quest Chains.",
		applicationName: "Onward Documentation",
	},
	canonicalUrlOrigin: siteOrigin,
	header: {
		navigation: [
			{ label: "Public docs", to: "/docs/introduction" },
			{ label: "Internal docs", to: "/dev-docs/introduction" },
		],
		themeSwitcher: { enabled: false },
	},
	authentication: {
		type: "openid",
		issuer: zitadelIssuer,
		clientId: zitadelClientId,
		scopes: ["openid", "profile", "email", "offline_access"],
		disableSignUp: true,
		redirectToAfterSignOut: "/docs/introduction",
	},
	protectedRoutes: ["/dev-docs/*"],
	navigation: [
		{
			type: "category",
			label: "Guides",
			items: [
				"/docs/introduction",
				"/docs/authentication",
				{
					type: "category",
					label: "Partner integrations",
					items: [
						"/docs/platform-integration",
						"/docs/advertiser-integration",
					],
				},
				{
					type: "category",
					label: "Account linking",
					items: [
						"/docs/account-linking",
						"/docs/account-linking/oauth-authorization-code",
						"/docs/account-linking/signed-token-callback",
						"/docs/account-linking/retroactive-events",
						"/docs/account-linking/security-requirements",
					],
				},
				{
					type: "category",
					label: "Rewarding user",
					items: ["/docs/rewards-and-webhooks"],
				},
				"/docs/errors",
			],
		},
		{
			type: "link",
			to: "/docs/api-reference",
			label: "Public API Reference",
		},
		{
			type: "category",
			label: "Internal documentation",
			items: [
				"/dev-docs/introduction",
				{
					type: "link",
					to: "/dev-docs/api-reference",
					label: "Internal API Reference",
				},
			],
		},
	],
	redirects: [
		{ from: "/", to: "/docs/introduction" },
		{ from: "/docs", to: "/docs/introduction" },
	],
	apis: [
		{
			type: "raw",
			input: JSON.stringify(withAudienceBadges(publicOpenApi)),
			path: "/docs/api-reference",
			options: {
				disablePlayground: true,
				disableSecurity: false,
				examplesLanguage: "shell",
				supportedLanguages: [
					{ value: "shell", label: "cURL" },
					{ value: "javascript", label: "JavaScript" },
				],
				showInfoPage: true,
				schemaDownload: { enabled: true, fileName: "onward-public-openapi" },
			},
		},
		{
			type: "file",
			input: "./apis/internal-openapi.json",
			path: "/dev-docs/api-reference",
			options: {
				disablePlayground: true,
				disableSecurity: false,
				examplesLanguage: "shell",
				supportedLanguages: [
					{ value: "shell", label: "cURL" },
					{ value: "javascript", label: "JavaScript" },
				],
				showInfoPage: true,
				schemaDownload: { enabled: false },
			},
		},
	],
	docs: {
		files: "/pages/**/*.{md,mdx}",
	},
	mdx: {
		components: {
			AudienceBadge,
			Mermaid,
			li: BabelSharkListItem,
			p: BabelSharkParagraph,
		},
	},
	theme: {
		light: darkTheme,
		dark: darkTheme,
		fonts: {
			sans: {
				url: "https://fonts.googleapis.com/css2?family=Bellota+Text:ital,wght@0,300;0,400;0,700;1,300;1,400;1,700&display=swap",
				fontFamily: '"Bellota Text", system-ui, sans-serif',
			},
			mono: "JetBrains Mono",
		},
	},
	syntaxHighlighting: {
		languages: ["http", "json", "shellscript", "javascript"],
		themes: {
			light: "dark-plus",
			dark: "dark-plus",
		},
	},
};

export default config;
